// Data for the dynamic Products pages, served at /products/:slug via ProductPage.jsx
import {
  Cloud, Server, RefreshCw, Timer, ShieldCheck, Gauge, Users,
  Target, Eye,
  Code2, CheckCircle2, Terminal,
  FolderGit2, ClipboardCheck, Sparkles,
} from "lucide-react";

export const PRODUCTS_DATA = {
  "cloud-labs": {
    label: "Cloud Labs",
    eyebrow: "CLOUD LABS",
    title: "Hands-on cloud labs. Real practice. Measurable skills.",
    subtitle:
      "Give every learner access to real cloud environments where they can practice, experiment, troubleshoot, and apply their knowledge — without the complexity of managing individual cloud accounts.",
    highlights: ["Real Cloud Environments", "Multi-Cloud Practice", "Guided Labs", "Auto Provisioning"],

    stats: [
      { value: "250+", label: "Enterprise Clients" },
      { value: "4,000+", label: "Professionals Upskilled" },
      { value: "98%", label: "Client Satisfaction" },
      { value: "3", label: "Major Clouds Supported" },
    ],

    faqs: [
      { q: "What are Skillsgain Cloud Labs?", a: "Sandboxed, real cloud environments that let learners practice hands-on across AWS, Microsoft Azure, and Google Cloud — without managing individual cloud accounts." },
      { q: "Which cloud platforms are covered?", a: "Cloud Labs support AWS, Microsoft Azure, and Google Cloud, so learners can practice across the platforms your organization actually uses." },
      { q: "Can Cloud Labs be combined with instructor-led training?", a: "Yes. Cloud Labs work during instructor-led programs or as self-paced practice, so learners can apply concepts on their own schedule." },
      { q: "How is cloud spend controlled?", a: "Every lab environment is time-boxed and automatically expires at the end of the session, so there's no manual cleanup or leftover cost." },
      { q: "Are Cloud Labs suitable for enterprise training programs?", a: "Yes — Cloud Labs are built for enterprise L&D and technical training teams, with secure, permissioned environments for large learner groups." },
    ],

    whyMattersHeading: "The gaps teams face without hands-on practice.",
    whyMatters: [
      "Learners understand concepts but often lack opportunities to practice them in real cloud environments.",
      "Setting up individual cloud accounts, permissions, resources, and environments can consume valuable instructor and IT time.",
      "Without defined limits and monitoring, cloud labs can create unnecessary infrastructure costs and security concerns.",
    ],

    whatHeading: "Three pillars for practical cloud learning.",
    whatCards: [
      { icon: Server, title: "Sandboxed Environments", desc: "Give every learner an isolated environment to practice without affecting shared infrastructure or other learners." },
      { icon: Terminal, title: "Guided Labs", desc: "Step-by-step exercises help learners complete real-world cloud tasks with instructions, hints, and validation." },
      { icon: RefreshCw, title: "Auto Provisioning & Expiry", desc: "Environments can be provisioned on demand and automatically expire after the learning session, helping control cloud usage and costs." },
    ],

    capabilitiesHeading: "Everything learners need for meaningful hands-on practice.",
    capabilities: [
      { icon: Cloud, title: "Multi-Cloud Support", desc: "Practice across AWS, Microsoft Azure, Google Cloud and other technology environments." },
      { icon: Timer, title: "Time-Boxed Sessions", desc: "Define session durations and automatically close environments when the learning activity ends." },
      { icon: Terminal, title: "Guided Learning", desc: "Structured instructions, practical scenarios, hints and task-based exercises." },
      { icon: ShieldCheck, title: "Secure Access", desc: "Controlled learning environments with defined permissions and access boundaries." },
      { icon: Gauge, title: "Real Infrastructure", desc: "Learners work with actual cloud services instead of simulated screens or theoretical exercises." },
      { icon: Users, title: "Instructor + Self-Paced", desc: "Use Cloud Labs during instructor-led programs or allow learners to practice independently." },
    ],

    howHeading: "From lab selection to hands-on practice in four simple steps.",
    howSteps: [
      { title: "Choose a Cloud Lab", desc: "Select a practical lab from the available learning catalogue." },
      { title: "Launch the Environment", desc: "Learners receive a ready-to-use cloud environment in seconds." },
      { title: "Complete Guided Tasks", desc: "Learners follow practical exercises and work directly with real cloud services." },
      { title: "Environment Expires Automatically", desc: "Once the session ends, the environment is automatically closed to help control usage and costs." },
    ],

    outcomesHeading: "Make hands-on learning easier to manage.",
    outcomes: [
      "Learners gain greater confidence through practical, hands-on application — not just theory.",
      "More learners can access practical environments than with instructor-managed lab setups.",
      "Clear, task-based evidence of practical skill through lab completion and guided activities.",
    ],

    techEcosystemHeading: "Practical learning across leading technology platforms.",
    techEcosystem: ["AWS", "Microsoft Azure", "Google Cloud", "Kubernetes", "Databricks", "Snowflake"],

    whoHeading: "Designed for teams that need practical skills.",
    whoItems: [
      { title: "Enterprise L&D Teams", desc: "Deliver hands-on learning experiences across large learner groups without managing individual cloud accounts." },
      { title: "IT & Cloud Teams", desc: "Help technical teams practice cloud technologies in controlled environments." },
      { title: "Technical Training Providers", desc: "Add real infrastructure practice to instructor-led and online training programs." },
      { title: "Instructors & Trainers", desc: "Spend more time teaching and less time setting up and managing lab environments." },
    ],

    ctaHeadline: "Give every learner a real cloud environment — not just a demonstration.",
    ctaSupporting: "Enable practical learning with Skillsgain Cloud Labs.",

    exploreHeading: "Explore Skillsgain's Learning Ecosystem",
    exploreCards: [
      { title: "Enterprise Learning Programs", desc: "Customized technology training programs designed around your organization's learning requirements.", href: "/solution/corporate-training" },
      { title: "AI & GenAI Training", desc: "Practical AI and Generative AI learning programs for enterprise teams.", href: "/skillsprogram/genai" },
      { title: "Technology Training", desc: "Instructor-led and hands-on training across leading technology domains.", href: "/offerings/all" },
      { title: "Skill Assessments", desc: "Assess technical capabilities and identify areas for focused learning.", href: "/products/ai-powered-assessments" },
    ],
  },

  "ai-powered-assessments": {
    label: "AI Powered Assessments",
    eyebrow: "SKILL VALIDATION WITH INTELLIGENCE",
    title: "AI-Powered Assessments",
    heroSubheadline: "Validate skills. Identify capability. Make better decisions.",
    subtitle:
      "Go beyond traditional MCQs with AI-powered assessments designed to evaluate practical knowledge, problem-solving ability, and role-specific skills.",
    highlights: ["Adaptive Testing", "AI Proctoring", "Role-Fit Scoring", "Assessment Integrity"],

    stats: [
      { value: "250+", label: "Enterprise Clients" },
      { value: "4,000+", label: "Professionals Upskilled" },
      { value: "200+", label: "Assessment Programs" },
      { value: "98%", label: "Client Satisfaction" },
    ],

    differenceEyebrow: "THE DIFFERENCE",
    differenceHeading: "Assess What People Can Actually Do.",
    differenceCopy:
      "Skillsgain assessments go beyond theory to evaluate practical knowledge, problem-solving ability, and role-specific skills — helping organizations hire, upskill, and deploy talent with greater confidence.",
    differenceCtaLabel: "Learn More",
    differenceCtaHref: "/solution/proctored-assessments",

    capabilitiesHeading: "Enterprise-Grade Assessment, End to End.",
    capabilitiesIntro:
      "Adaptive testing, practical challenges, AI-assisted proctoring, and role-based scoring designed for enterprise assessment programs.",
    capabilities: [
      { icon: Gauge, title: "Adaptive Testing", desc: "Question difficulty adapts to candidate performance for a sharper skill signal." },
      { icon: Code2, title: "Practical Challenges", desc: "Use case-based, coding, and scenario-driven assessments to evaluate application." },
      { icon: Eye, title: "AI Proctoring", desc: "Support identity verification, environment checks, and behavioral monitoring." },
      { icon: Target, title: "Role-Based Scoring", desc: "Map performance to defined roles and competency frameworks." },
    ],
    capabilitiesCtaLabel: "Explore All Capabilities",
    capabilitiesCtaHref: "/solution/proctored-assessments",

    howHeading: "From Assessment to Action.",
    howSteps: [
      { title: "Define", desc: "Identify the role, competency, or skill to evaluate." },
      { title: "Assess", desc: "Conduct adaptive and practical assessments." },
      { title: "Analyze", desc: "Review performance and skill insights." },
      { title: "Decide", desc: "Use results to support hiring, learning, or deployment decisions." },
    ],

    outcomesHeading: "Better Skill Signals. Better Talent Decisions.",
    outcomes: [
      "Reduce screening and interview effort by identifying stronger candidates earlier.",
      "Improve hiring quality with practical, proficiency-focused evidence.",
      "Support internal mobility by identifying employees with relevant capabilities.",
      "Use assessment insights to guide targeted learning and development.",
      "Maintain stronger assessment integrity through AI-assisted monitoring.",
    ],

    whoEyebrow: "USE CASES",
    whoHeading: "One Platform. Multiple Talent Decisions.",
    whoItems: [
      { title: "Pre-Hire Assessments", desc: "Screen candidates using role-relevant, practical assessments." },
      { title: "Technical Assessments", desc: "Evaluate technical capabilities through practical challenges." },
      { title: "Internal Skill Assessments", desc: "Understand employee capabilities and skill gaps." },
      { title: "Upskilling & Reskilling", desc: "Identify learning needs and measure progress." },
      { title: "Certification Readiness", desc: "Check knowledge and practical readiness before certification." },
      { title: "Campus & Graduate Hiring", desc: "Assess large candidate pools with structured evaluation." },
    ],

    testimonial: {
      quote: "Skillsgain assessments gave us a clearer view of practical capabilities and helped us make more confident talent decisions.",
      attribution: "— L&D Leader, Global Enterprise",
    },

    ctaHeadline: "Ready to Assess Skills With Greater Confidence?",
    ctaSupporting:
      "Share your assessment requirements and our enterprise team will help design an assessment experience aligned with your roles, competencies, and business goals.",

    exploreHeading: "Other Skillsgain Products",
    exploreCards: [
      { title: "Enterprise Learning", desc: "Enterprise learning programs for modern teams.", href: "/solution/corporate-training" },
      { title: "eLearning", desc: "Modular digital learning content across technology and business domains.", href: "/products/e-learning" },
      { title: "AWS Cloud Labs", desc: "Hands-on AWS environments for practical cloud learning.", href: "/products/cloud-labs" },
      { title: "AI-Powered Coding", desc: "Practical coding assessments for technical talent.", href: "/products/ai-powered-coding-platform" },
    ],
  },

  "ai-powered-coding-platform": {
    label: "AI Powered Coding Platform",
    eyebrow: "PRODUCT · AI-POWERED CODING PLATFORM",
    title: "AI-Powered Coding Platform",
    heroSubheadline: "Evaluate developers on the skills that matter.",
    subtitle:
      "Skillsgain's AI-Powered Coding Platform helps enterprises assess technical capabilities through real-world coding environments, multi-file projects, automated evaluation, and AI-aware insights.",
    highlights: ["40+ Programming Languages", "Real-World Code Projects", "AI-Aware Evaluation", "Automated Grading", "Enterprise-Ready"],

    whyEyebrow: "BUILT FOR MODERN TECHNICAL ASSESSMENTS",
    whyHeading: "Why Skillsgain",
    whyCopy:
      "A comprehensive coding assessment platform designed for hiring, internal skill evaluation, and learning programs — built for modern engineering teams.",
    whyCopy2:
      "From real-world coding challenges to automated grading and expert review workflows, Skillsgain helps enterprises measure true technical capability at scale, with accuracy and confidence.",
    whyCtaLabel: "See How It Works",
    whyCtaHref: "/solution/proctored-assessments",

    capabilitiesHeading: "Core Capabilities",
    capabilities: [
      { icon: Code2, title: "Real-World Coding Challenges", desc: "Practical, scenario-based assignments that reflect actual engineering work." },
      { icon: FolderGit2, title: "Multi-File Project Assessments", desc: "Assess how developers navigate, modify, and work with real repositories." },
      { icon: Terminal, title: "40+ Language Runtimes", desc: "Support for popular programming languages, frameworks, and technology stacks." },
      { icon: Sparkles, title: "AI-Aware Evaluation", desc: "Detect and account for AI-tool usage patterns so enterprises can gain more meaningful assessment signals." },
      { icon: CheckCircle2, title: "Automated Code Evaluation", desc: "Test cases, static analysis, code quality, and complexity checks." },
      { icon: ClipboardCheck, title: "Expert Review Workflows", desc: "Enable technical reviewers to assess code, add comments, and use structured evaluation rubrics." },
    ],

    outcomesEyebrow: "BUSINESS OUTCOMES",
    outcomesHeading: "Measure What Matters",
    outcomesIntro: "Skillsgain helps enterprises make technical assessments faster, fairer, and more relevant.",
    outcomes: [
      "Better technical hiring decisions",
      "Faster screening at scale",
      "Consistent and standardized evaluation",
      "Clear skill visibility and development insights",
      "Scalable assessment for high-volume hiring",
      "Complete assessment records for every submission",
    ],

    calloutHeadingA: "From Code to",
    calloutHeadingB: "Capability",
    calloutCopy: "Go beyond theoretical questions. Assess how developers solve real problems, work with existing code, and deliver functional solutions.",
    calloutStats: ["40+ Language Runtimes", "Secure & Reliable", "Built for Enterprise Scale"],

    whoEyebrow: "DESIGNED FOR ENTERPRISE USE",
    whoHeading: "One Platform. Multiple Use Cases.",
    whoIntro: "Whether you're hiring, upskilling, or certifying, Skillsgain adapts to your technical assessment goals.",
    whoItems: [
      { title: "Technical Hiring", desc: "Evaluate technical candidates through real-world coding challenges aligned with your technology stack. Identify talent based on demonstrated skills instead of resumes alone." },
      { title: "Internal Skill Assessment", desc: "Measure engineering team capabilities and identify technical skill gaps that can be addressed through targeted learning." },
      { title: "Learning & Development", desc: "Use assessments before and after training programs to measure learning progress and technical improvement." },
      { title: "Certification & Skill Validation", desc: "Validate coding proficiency through practical assessments instead of relying only on knowledge-based testing." },
      { title: "Campus & Early-Career Hiring", desc: "Screen large candidate pools using standardized coding challenges and practical programming evaluations." },
    ],

    exploreEyebrow: "EXPLORE SKILLSGAIN",
    exploreHeading: "Our Enterprise Learning & Assessment Platform",
    exploreIntro: "Discover a complete suite of products and services designed to help enterprises develop and validate technical capabilities at scale.",
    exploreCards: [
      { title: "AI-Powered Coding Platform", desc: "Practical coding assessments for real engineering skills.", href: "/products/ai-powered-coding-platform", current: true },
      { title: "AI-Powered Assessments", desc: "Intelligent assessments for technical and professional capabilities.", href: "/products/ai-powered-assessments" },
      { title: "Enterprise Learning", desc: "Structured learning programs for organizational capability development.", href: "/solution/corporate-training" },
      { title: "Hands-On Labs", desc: "Real technical environments for practical learning and experience.", href: "/products/cloud-labs" },
      { title: "Corporate Training", desc: "Instructor-led and customized learning programs for enterprise teams.", href: "/solution/corporate-training" },
    ],

    ctaHeadline: "Ready to Evaluate Technical Skills with Greater Confidence?",
    ctaSupporting:
      "Share a few details about your hiring, assessment, or learning requirements. Our team will help you design an assessment approach aligned with your technology stack and business objectives.",
  },
};
